import { countPriorPosts, describePriorPosts } from '@lib/social/scheduling/priorPosts';
import { OpenInNewRounded } from '@mui/icons-material';
import { Button, IconButton } from '@mui/material';
import type { SocialConnection } from '@stores/SocialConnections';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { PostChip } from '../components/PostChip/PostChip';
import { PostListPopover } from '../components/PostListPopover/PostListPopover';
import { usePostForm } from './PostFormStore';
import css from './PriorPostsLine.module.scss';

/**
 * Under an account's name: what this render already did there, as a line
 * that opens the posts themselves. Each row opens the post's page in a new
 * tab (the form keeps what was typed); a published row also links to the
 * post on the platform. Nothing at all for an account the render never went
 * to, which is most of them.
 */
export const PriorPostsLine = observer(function PriorPostsLine({
  connection,
  name,
}: {
  connection: SocialConnection;
  /** The name the card shows, repeated as the list's title. */
  name: string;
}) {
  const form = usePostForm();
  const posts = form.priorPosts(connection.id);
  const line = describePriorPosts(countPriorPosts(posts));
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  if (!line) return null;
  return (
    <>
      <Button
        variant="text"
        size="small"
        className={css.line}
        onClick={(e) => {
          // Nested in the foldable header: the click opens the list, not the fold.
          e.stopPropagation();
          setAnchor(e.currentTarget);
        }}
      >
        {line}
      </Button>
      <PostListPopover
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        header={
          <div className={css.header}>
            <span className={css.name}>{name}</span>
            <span className={css.summary}>{line}</span>
          </div>
        }
      >
        {posts.map((post) => (
          <div key={post.id} className={css.row}>
            <PostChip post={post} link="new-tab" showDate />
            {post.permalink && (
              <IconButton
                component="a"
                size="small"
                className={css.permalink}
                href={post.permalink}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="View on the platform"
                title="View on the platform"
              >
                <OpenInNewRounded />
              </IconButton>
            )}
          </div>
        ))}
      </PostListPopover>
    </>
  );
});
